import { OptionSummary } from '@/services/BookingService.interface'
import { Service } from '@/services/CatalogService.interface'
import { PrestType } from '@/config'
import { ServiceType } from '@/config'
import { getAirportName, getTrainStationName, getTrainCompanyName } from '@/utils/locationCodes'
import { FamilyIds } from '@/assets/families'
import {
  getLuggageDetails,
  resolveLuggageTripInfo,
  type LuggageOptionLike,
} from '@/utils/luggageTripInformation'

// Helper function to check if a name is generic
function isGenericName(name: string): boolean {
  const genericNames = ['flight', 'transfer', 'luggage', 'train', 'transfert']
  return genericNames.includes(name.toLowerCase())
}

// Helper function to generate proper name from trip information
function generateNameFromTripInfo(tripInformation: any, familyId?: number): string {
  if (Array.isArray(tripInformation) && tripInformation.length > 0) {
    // Check for the correct structure: tripInformation[0].details
    const tripInfo = tripInformation[0].details || tripInformation[0].tripInformation
    return generateNameFromTripData(tripInfo, familyId)
  }
  return generateNameFromTripData(tripInformation, familyId)
}

// Helper function to generate name from trip data
function generateNameFromTripData(tripData: any, familyId?: number): string {
  if (!tripData) return 'Unknown Service'
  
  switch (familyId) {
    case FamilyIds.FLIGHT: // Flight
      if (tripData.outboundTrip) {
        const outbound = tripData.outboundTrip
        const departure = outbound.departure?.location || ''
        const arrival = outbound.arrival?.location || ''
        const company = outbound.company || ''
        return `${company} ${departure} → ${arrival}`
      }
      return 'Flight'
      
    case FamilyIds.TRAIN: // Train (familyId 8)
    case FamilyIds.TRAIN_ALTERNATIVE: // Train (alternative familyId)
      if (tripData.outboundTrip) {
        const outbound = tripData.outboundTrip
        const departure = getTrainStationName(outbound.departure?.location || '')
        const arrival = getTrainStationName(outbound.arrival?.location || '')
        const company = getTrainCompanyName(outbound.company || '')
        return `${company} ${departure} → ${arrival}`
      }
      return 'Train'
      
    case FamilyIds.TRANSFER: // Transfer
      if (tripData.transferType === 'TAXI') {
        return 'Taxi Transfer'
      } else if (tripData.transferType === 'BUS') {
        return 'Bus Transfer'
      } else if (tripData.origin && tripData.destination) {
        return `Transfer ${tripData.origin} → ${tripData.destination}`
      }
      return 'Transfer'
      
    case FamilyIds.LUGGAGE: // Luggage
      {
        const weight = tripData.weight ?? tripData.details?.weight
        const description = tripData.description ?? tripData.details?.description
        if (weight) return `${description || 'Luggage'} ${weight}kg`
      }
      return 'Luggage'
      
    default:
      return 'Unknown Service'
  }
}

function toLuggageOptionLike(option: OptionSummary): LuggageOptionLike {
  return {
    optionId: option.id,
    id: option.prestId,
    nameCommercial: option.name,
    sellingPrice: option.sellingPrice,
    quantity: option.quantity,
    familyId: option.familyId,
    tripInformation: option.tripInformation,
  }
}

export default class BookingServiceMapper {
  static mapServices(
    booking: { options: OptionSummary[] }, // Explicitly typing `booking`
    additionalServices: OptionSummary[],
    catalogServices: Service[]
  ): Service[] {
    /* ------------------- Accommodation Grouping ------------------- */
    const accommodationServices = booking.options.filter(
      (option: OptionSummary) => option.prestTypeId === PrestType.Accomodation
    )

    // Group accommodations by `nameCommercial`, summing the quantity
    const groupedAccommodations = new Map<string, Service>()

    accommodationServices.forEach((option: OptionSummary) => {
      const key = option.name || 'Unknown' // Ensure key is always a string

      if (groupedAccommodations.has(key)) {
        groupedAccommodations.get(key)!.quantity =
          (groupedAccommodations.get(key)?.quantity ?? 0) +
          (option.quantity ?? 1)
      } else {
        groupedAccommodations.set(key, {
          id: option.id ? parseInt(option.id, 10) : undefined, // Ensure `id` is number | undefined
          nameCommercial: option.name,
          familyId: option.familyId ?? undefined, // Ensure `number | undefined`
          quantity: option.quantity ?? 1,
          serviceType: ServiceType.ACCOMMODATION,
        })
      }
    })

    /* ------------------- Identify Missing Services ------------------- */
    const missingServices = additionalServices.filter(
      (option: OptionSummary) =>
        !catalogServices.some(service => service.id === option.prestId)
    )

    if (missingServices.length > 0) {
      console.error(
        `Missing services in catalog: ${missingServices.map(s => s.name).join(', ')}`
      )
    }

    // Convert missing services from OptionSummary to Service format
    const mappedMissingServices: Service[] = missingServices.map(option => ({
      id: option.prestId,
      optionId: option.id,
      sellingPrice: option.sellingPrice,
      nameCommercial: option.name ?? 'Unknown Service',
      familyId: option.familyId ?? undefined,
      quantity: option.quantity ?? 1,
      serviceType: ServiceType.ADDITIONAL,
      tripInformation: option.tripInformation, // Preserve tripInformation from OptionSummary
    }))

    /* ------------------- Additional Services Mapping ------------------- */
    // One row per option line (so e.g. x5 and x4 for same service name both appear)
    const mappedAdditionalServices: Service[] = additionalServices
      .filter(
        (option: OptionSummary) =>
          catalogServices.some((service: Service) => service.id === option.prestId)
      )
      .map((option: OptionSummary) => {
        const catalogService = catalogServices.find(
          (s: Service) => s.id === option.prestId
        )!
        let properName =
          option.name ?? catalogService.name ?? 'Unknown Service'
        if (option.tripInformation && isGenericName(properName)) {
          if (option.familyId === FamilyIds.LUGGAGE) {
            const luggageSiblings = additionalServices
              .filter(o => o.familyId === FamilyIds.LUGGAGE)
              .map(toLuggageOptionLike)
            const matched = resolveLuggageTripInfo(
              toLuggageOptionLike(option),
              luggageSiblings
            )
            const details = getLuggageDetails(matched)
            properName = details
              ? generateNameFromTripData(details, option.familyId)
              : properName
          } else {
            properName = generateNameFromTripInfo(
              option.tripInformation,
              option.familyId
            )
          }
        }
        return {
          id: option.prestId,
          optionId: option.id,
          sellingPrice: option.sellingPrice,
          nameCommercial: properName,
          familyId: option.familyId ?? undefined,
          quantity: option.quantity ?? 1,
          serviceType: ServiceType.ADDITIONAL,
          tripInformation: option.tripInformation,
        }
      })

    return [
      ...mappedAdditionalServices,
      ...mappedMissingServices,
      ...Array.from(groupedAccommodations.values()),
    ]
  }
}
